import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { BillProviderIcon } from '@/components/bills/BillProviderIcon';
import { Loader } from '@/components/ui/Loader';
import { cn } from '@/utils/cn';

export interface BillIconSelectOption {
  value: string;
  label: string;
  sublabel?: string;
  logoUrl?: string | null;
  fallbackColor?: string;
  fallbackInitial?: string;
  leadingIcon?: ReactNode;
}

interface BillIconSelectProps {
  label?: string;
  placeholder?: string;
  value: string;
  options: BillIconSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  sheetTitle?: string;
}

export function BillIconSelect({
  label,
  placeholder = 'Choose an option',
  value,
  options,
  onChange,
  disabled = false,
  loading = false,
  error,
  sheetTitle,
}: BillIconSelectProps) {
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(next: string) {
    onChange(next);
    setOpen(false);
    setSheetOpen(false);
  }

  function openPicker() {
    if (disabled || loading) return;
    if (window.innerWidth < 768) {
      setSheetOpen(true);
    } else {
      setOpen((prev) => !prev);
    }
  }

  function renderLeading(option: BillIconSelectOption) {
    if (option.leadingIcon) return option.leadingIcon;
    return (
      <BillProviderIcon
        logoUrl={option.logoUrl}
        fallbackColor={option.fallbackColor}
        fallbackInitial={option.fallbackInitial ?? option.label.charAt(0)}
        alt={option.label}
      />
    );
  }

  const listContent = loading ? (
    <div className="flex items-center justify-center p-4">
      <Loader label="Loading..." />
    </div>
  ) : error ? (
    <div className="p-4 text-sm text-red-600">{error}</div>
  ) : (
    <div className="p-2">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => handleSelect(option.value)}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors hover:bg-gray-50',
            value === option.value && 'bg-secondary-50',
          )}
        >
          {renderLeading(option)}
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-gray-900">{option.label}</div>
            {option.sublabel && (
              <div className="truncate text-sm text-gray-500">{option.sublabel}</div>
            )}
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}

      <div className="relative" ref={containerRef}>
        <button
          type="button"
          disabled={disabled || loading}
          onClick={openPicker}
          className={cn(
            'flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white p-3 text-left transition hover:bg-gray-50',
            'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20',
            (disabled || loading) && 'cursor-not-allowed opacity-50',
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            {selected ? (
              <>
                {renderLeading(selected)}
                <span className="truncate font-medium text-gray-900">{selected.label}</span>
              </>
            ) : (
              <span className="text-gray-500">{loading ? 'Loading...' : placeholder}</span>
            )}
          </div>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-gray-500 transition-transform', open && 'rotate-180')}
          />
        </button>

        {open && (
          <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
            {listContent}
          </div>
        )}
      </div>

      {sheetOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
            onClick={() => setSheetOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[75vh] overflow-hidden rounded-t-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h3 className="font-semibold text-gray-900">{sheetTitle ?? label ?? 'Select'}</h3>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[calc(75vh-3.5rem)] overflow-y-auto">{listContent}</div>
          </div>
        </div>
      )}
    </div>
  );
}
